import { Helmet } from "react-helmet-async";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "../components/iconify";
import ShowProfileCompletionOverlaySettings from "../components/settings/ShowProfileCompletionOverlaySettings";
import EditProfileCompletionOverlayDialog from "../components/settings/EditProfileCompletionOverlayDialog";
import { useAdminSettings } from "../hooks/useAdminSettings";
import { ADMIN_SETTING_TYPES } from "../constants/adminSettings";

const defaultFormState = {
  id: null,
  platform: "",
  latest_version: "",
  force_update: false,
};

const normalizePlatform = (value) => value.trim().toLowerCase();

export default function SettingsPage() {
  const { settings, loading, saving, fetchSettings, saveSetting } =
    useAdminSettings();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profileOverlayDialogOpen, setProfileOverlayDialogOpen] =
    useState(false);
  const [formState, setFormState] = useState({ ...defaultFormState });

  useEffect(() => {
    fetchSettings().catch(console.error);
  }, [fetchSettings]);

  const appVersionSettings = useMemo(
    () =>
      settings.filter(
        (item) => item.settingType === ADMIN_SETTING_TYPES.APP_VERSION,
      ),
    [settings],
  );

  const profileCompletionOverlaySetting = useMemo(
    () =>
      settings.find(
        (item) =>
          item.settingType === ADMIN_SETTING_TYPES.PROFILE_COMPLETION_OVERLAY,
      ),
    [settings],
  );

  const handleOpenDialog = (setting) => {
    if (setting) {
      setFormState({
        id: setting.id,
        platform: setting.settingValue?.platform || "",
        latest_version: setting.settingValue?.latest_version || "",
        force_update: Boolean(setting.settingValue?.force_update),
      });
    } else {
      setFormState({ ...defaultFormState });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormState({ ...defaultFormState });
  };

  const handleChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formState.platform || !formState.latest_version) {
      return;
    }

    try {
      await saveSetting({
        id: formState.id ?? undefined,
        settingType: ADMIN_SETTING_TYPES.APP_VERSION,
        settingValue: {
          platform: normalizePlatform(formState.platform),
          latest_version: formState.latest_version.trim(),
          force_update: Boolean(formState.force_update),
        },
      });
      handleCloseDialog();
    } catch (error) {
      console.error("Unable to save app version setting", error);
    }
  };

  const handleSaveProfileCompletionOverlay = async (settingValue) => {
    try {
      await saveSetting({
        id: profileCompletionOverlaySetting?.id ?? undefined,
        settingType: ADMIN_SETTING_TYPES.PROFILE_COMPLETION_OVERLAY,
        settingValue,
      });
      setProfileOverlayDialogOpen(false);
    } catch (error) {
      console.error("Unable to save profile completion overlay setting", error);
    }
  };

  return (
    <>
      <Helmet>
        <title> Settings </title>
      </Helmet>

      <Container maxWidth="md">
        <Stack spacing={4}>
          <Box>
            <Typography variant="h4">Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage app settings and version requirements.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card
                sx={{
                  position: "relative",
                  height: "180px",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  boxShadow: 3,
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease-in-out",
                }}
              >
                <IconButton
                  onClick={() => setProfileOverlayDialogOpen(true)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "primary.main",
                      color: "white",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  size="small"
                >
                  <Iconify icon="eva:edit-fill" width={18} />
                </IconButton>

                <Typography
                  variant="subtitle1"
                  sx={{
                    p: 2,
                    pb: 1,
                    textAlign: "center",
                    fontWeight: 600,
                    color: "primary.main",
                    fontSize: "0.9rem",
                  }}
                >
                  Show Profile Completion Overlay
                </Typography>

                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <ShowProfileCompletionOverlaySettings
                    settingValue={profileCompletionOverlaySetting?.settingValue}
                  />
                )}
              </Card>
            </Grid>
          </Grid>

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h5">App Versions</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage app version requirements for each platform.
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => handleOpenDialog(null)}
              >
                Add Platform
              </Button>
            </Stack>

            <Card>
              {loading ? (
                <Box
                  sx={{ display: "flex", justifyContent: "center", p: 4 }}
                >
                  <CircularProgress />
                </Box>
              ) : appVersionSettings.length === 0 ? (
                <Box sx={{ p: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No app version settings configured yet.
                  </Typography>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell>Latest Version</TableCell>
                      <TableCell>Force Update</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {appVersionSettings.map((setting) => (
                      <TableRow key={setting.id}>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {setting.settingValue?.platform}
                        </TableCell>
                        <TableCell>
                          {setting.settingValue?.latest_version}
                        </TableCell>
                        <TableCell>
                          {setting.settingValue?.force_update ? "Yes" : "No"}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenDialog(setting)}
                          >
                            <Iconify icon="eva:edit-fill" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </Box>
        </Stack>
      </Container>

      <EditProfileCompletionOverlayDialog
        open={profileOverlayDialogOpen}
        onClose={() => setProfileOverlayDialogOpen(false)}
        settingValue={profileCompletionOverlaySetting?.settingValue}
        isSaving={saving}
        onSave={handleSaveProfileCompletionOverlay}
      />

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formState.id ? "Edit App Version" : "Add App Version"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Platform"
              value={formState.platform}
              onChange={(event) => handleChange("platform", event.target.value)}
              fullWidth
              required
              helperText="Example: ios, android"
              disabled={saving}
            />

            <TextField
              label="Latest Version"
              value={formState.latest_version}
              onChange={(event) =>
                handleChange("latest_version", event.target.value)
              }
              fullWidth
              required
              helperText="Semantic version format, e.g. 1.2.3"
              disabled={saving}
            />

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle1">Force Update</Typography>
              <Switch
                checked={Boolean(formState.force_update)}
                onChange={(event) =>
                  handleChange("force_update", event.target.checked)
                }
                disabled={saving}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !formState.platform || !formState.latest_version}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
